import { useEffect } from 'react';
import type { LandingPageSeoConfig } from './LandingPageSeoInterfaces';
import { DEFAULT_OG_IMAGE_PATH, WORKY_SITE_ORIGIN, homeSeo } from './LandingPageSeoConfig';

const SEO_LINK_REL: string = 'canonical';
const SEO_LINK_DATA_ATTR: string = 'data-worky-seo';
const SEO_JSON_LD_ID_PREFIX: string = 'worky-landing-jsonld-';

const upsertMetaContent = (
  attributeName: 'name' | 'property',
  attributeValue: string,
  content: string
): HTMLMetaElement => {
  const selector: string = `meta[${attributeName}="${attributeValue}"]`;
  const existing: Element | null = document.querySelector(selector);
  const metaElement: HTMLMetaElement =
    existing instanceof HTMLMetaElement
      ? existing
      : ((): HTMLMetaElement => {
          const created: HTMLMetaElement = document.createElement('meta');
          created.setAttribute(attributeName, attributeValue);
          document.head.appendChild(created);
          return created;
        })();
  metaElement.setAttribute('content', content);
  return metaElement;
};

const upsertCanonicalLink = (href: string): HTMLLinkElement => {
  const existingLinks: NodeListOf<Element> = document.querySelectorAll(`link[rel="${SEO_LINK_REL}"]`);
  let primaryLink: HTMLLinkElement | null = null;

  existingLinks.forEach((element: Element, index: number): void => {
    if (!(element instanceof HTMLLinkElement)) {
      return;
    }
    if (index === 0) {
      primaryLink = element;
      element.setAttribute('href', href);
      element.setAttribute(SEO_LINK_DATA_ATTR, 'true');
      return;
    }
    element.parentNode?.removeChild(element);
  });

  if (primaryLink !== null) {
    return primaryLink;
  }

  const created: HTMLLinkElement = document.createElement('link');
  created.setAttribute('rel', SEO_LINK_REL);
  created.setAttribute(SEO_LINK_DATA_ATTR, 'true');
  created.setAttribute('href', href);
  document.head.appendChild(created);
  return created;
};

const buildCanonicalUrl = (canonicalPath: string): string => {
  const normalizedPath: string =
    canonicalPath === '/' ? '/' : canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`;
  return normalizedPath === '/' ? `${WORKY_SITE_ORIGIN}/` : `${WORKY_SITE_ORIGIN}${normalizedPath}`;
};

const buildOgImageUrl = (ogImagePath: string | undefined): string => {
  const path: string = ogImagePath ?? DEFAULT_OG_IMAGE_PATH;
  return path.startsWith('http') ? path : `${WORKY_SITE_ORIGIN}${path}`;
};

const removeManagedJsonLdScripts = (): void => {
  const existingScripts: NodeListOf<Element> = document.querySelectorAll(
    `script[type="application/ld+json"][id^="${SEO_JSON_LD_ID_PREFIX}"]`
  );
  existingScripts.forEach((element: Element): void => {
    element.parentNode?.removeChild(element);
  });
};

const upsertManagedJsonLdScripts = (
  jsonLdItems: readonly Readonly<Record<string, unknown>>[] | undefined
): void => {
  removeManagedJsonLdScripts();
  if (!jsonLdItems || jsonLdItems.length === 0) {
    return;
  }

  jsonLdItems.forEach((payload: Readonly<Record<string, unknown>>, index: number): void => {
    const scriptElement: HTMLScriptElement = document.createElement('script');
    scriptElement.id = `${SEO_JSON_LD_ID_PREFIX}${index}`;
    scriptElement.type = 'application/ld+json';
    scriptElement.textContent = JSON.stringify(payload);
    document.head.appendChild(scriptElement);
  });
};

const applyLandingPageSeo = (config: LandingPageSeoConfig): void => {
  const canonicalUrl: string = buildCanonicalUrl(config.canonicalPath);
  const ogImageUrl: string = buildOgImageUrl(config.ogImagePath);
  const ogType: string = config.ogType ?? 'website';

  document.title = config.title;
  upsertMetaContent('name', 'description', config.description);
  if (config.keywords) {
    upsertMetaContent('name', 'keywords', config.keywords);
  }
  upsertCanonicalLink(canonicalUrl);
  upsertMetaContent('property', 'og:url', canonicalUrl);
  upsertMetaContent('property', 'og:type', ogType);
  upsertMetaContent('property', 'og:title', config.title);
  upsertMetaContent('property', 'og:description', config.description);
  upsertMetaContent('property', 'og:image', ogImageUrl);
  upsertMetaContent('name', 'twitter:card', 'summary_large_image');
  upsertMetaContent('name', 'twitter:url', canonicalUrl);
  upsertMetaContent('name', 'twitter:title', config.title);
  upsertMetaContent('name', 'twitter:description', config.description);
  upsertMetaContent('name', 'twitter:image', ogImageUrl);
  upsertManagedJsonLdScripts(config.jsonLd);
};

const useLandingPageSeo = (config: LandingPageSeoConfig): void => {
  useEffect((): (() => void) => {
    applyLandingPageSeo(config);
    return (): void => {
      removeManagedJsonLdScripts();
      applyLandingPageSeo(homeSeo);
    };
  }, [
    config.title,
    config.description,
    config.canonicalPath,
    config.ogImagePath,
    config.keywords,
    config.ogType,
    config.jsonLd,
  ]);
};

export { applyLandingPageSeo, useLandingPageSeo };
